/** The itinerary record's binding of the records core (plan §5, §6, §7, §8, §9.8, §10.3; O9,
 *  O10): what the page's `DraftProvider` and `RecordEditorProvider` need, in view mode and in
 *  edit mode.
 *
 *  - **Baselines** — the editable fields' stored values in the changeset's wire form, the
 *    itinerary's on `itinerary:<group id>` (group and catalog-itinerary fields alike, O10) and
 *    each day's on `day:<id>`. They come from edit_context; until it arrives, the same values
 *    read from the detail (`itineraryBaselines`), so the cards render at once — but the route
 *    map's and the days' images are their CDN keys there (their wire form is an Image id, which
 *    only edit_context carries): nothing can change them before edit_context is here.
 *  - **Fields** — the server's edit catalog (`catalogRecordField`: the itinerary's fields, then
 *    a day's, told apart by entity), else the registries, which agree with it (the contract
 *    test).
 *  - **Verdicts (D4)** — edit_context `feed.verdicts` for the itinerary's fields, and each day's
 *    title verdict (`feed.day_verdicts`: the feed matches days by title) for its title; a
 *    changed field reads the verdict the PREVIEW gives its new value. The feed names the
 *    record's "managed by" only while its source may update itineraries.
 *  - **Editability** — the `ConsoleCatalogEditEnabled` kill switch ("Editing is switched off"),
 *    `console_itineraries_manage`, an owning operator, each field's guards (the placeholder's
 *    `name` and `published`, O9, from edit_context `blocked`).
 *  - **Preview / apply** — the itinerary changeset's, the wire built with edit_context's
 *    baselines (a removed day is compare-and-set on its six fields). A removed day another
 *    editor changed meanwhile (the preview's drift, the apply's 409) is brought back as saved
 *    now, said in the Days card's live region (`daySave`); the save's new days' real ids
 *    (`created`) re-key what the draft still holds of them inside the ledger's SAVED (over
 *    `memberships`, never an undo step of its own) and move the open day.
 *  - **The re-key question (O11)** — the catalog's `rekey_import_matching`, raised by a Custom
 *    ID change on an itinerary an import has touched (its `source`), asked as the activity's
 *    (`rekeyQuestion`): under the field and in the ledger in the server's words ("Re-key import
 *    matching to SEA-1? Intrepid's next sync will create a new record."); Save waits for it.
 *    **Re-key** is bound to the Custom ID it was given for and sent with it
 *    (`rekey_import_matching_to`); **Cancel** takes the Custom ID change back. Clearing a
 *    Custom ID while the import identity is set is the server's refusal, under the field.
 *  - **The days** (O7) — a new day reads `NEW_DAY` until edited (`defaultValueOf`); reverting
 *    the day list also reverts the new days' own edits (`cascadeTargets`); each day's edits
 *    are a ledger group named like its row.
 *  - **Names** — the picked records the detail names (currency, tour, line set, the id sets);
 *    the pickers add what the operator picks.
 *  - **The App button** (§5, C8) — a field the behaviour does not use (the draft's behaviour,
 *    or the operator's when it inherits) is kept, dimmed, "Not used with <behaviour>"; with the
 *    Phone behaviour the Booking URL holds the `tel:` link it dials (`itineraryFieldNote`). The
 *    server's App button rule — a button with a text that opens a link needs the Booking URL,
 *    one that adds or shows a tour needs the tour — comes back as the preview's refusal under
 *    the field, only while the button has a text. */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { CatalogDriftError } from '@/api/catalogRecord';
import type { ItineraryDetail } from '@/api/itineraries';
import {
  DAY_CHANGE_ENTITY,
  DAYS_FIELD,
  itineraryRecordKeys,
  useApplyItineraryChangeset,
  useItineraryChangesetPreview,
  type ItineraryBaselines,
  type ItineraryRecordCatalog,
  type ItineraryRecordEditContext,
} from '@/api/itineraryRecord';
import { useSnackbar } from '@/components/Snackbar';
import { CATALOG_EDITING_OFF, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { PLACEHOLDER_LOCKED } from '@/features/catalog/words';
import { catalogRecordField } from '@/features/catalog/recordFields';
import { rekeyAskedUpFront, rekeyQuestionOptions } from '@/features/catalog/rekeyQuestion';
import {
  draftCount,
  parseRecordKey,
  type DraftAction,
  type DraftState,
  type DraftTarget,
  type RecordChangeset,
  type RecordKey,
} from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import type {
  RecordApplyMutation,
  RecordApplyResult,
  RecordEditability,
  RecordEditorConfig,
  RecordField,
  RecordOption,
  RecordOptionRef,
  RecordPreviewQuery,
  RecordVerdict,
} from '@/features/records/editor/types';
import type { OutlineOpsContext } from '@/features/records/outline/ops';

import { itineraryFieldNote } from './cards/appButton';
import { dayNameIn, restoreDay } from './days/dayOps';
import { driftedRemovedDays, namedRemovalsInPreview, namedRemovalsInResult, removedDaysDrifted } from './days/daySave';
import type { DaysChannel } from './days/daysChannel';
import {
  DAYS_OUTLINE,
  dayRec,
  dayRowName,
  dayRows,
  ITINERARY_MEMBERSHIPS,
  NEW_DAY,
  storedDayNames,
  type DayRowModel,
} from './days/daysModel';
import { itineraryTitle } from './parts/ItineraryRecordHeader';
import { ITINERARY_DAY_ENTITY, ITINERARY_ENTITY, itineraryDayRecordFields, itineraryRecordFields } from './registry';
import { formatItineraryValue, itineraryRec } from './viewConfig';
import { DAY_SPAN_WIDGET, storedImageOf, useItineraryWidgets, type ItineraryStoredImages } from './widgets';

export const ITINERARY_MANAGE_PERM = 'console_itineraries_manage';

/** The record's own times (a receipt, "changed on the server at …", a restored draft) read in
 *  UTC, labelled — as the activity's do. An itinerary has no wall-clock field of its own. */
export const ITINERARY_TIMEZONE = 'UTC';

/** Why the editor cannot open on this itinerary, or null — the permission aside (no
 *  permission, no Edit at all). The switch's words win: they say the environment, not the
 *  record. */
export function itineraryEditBlockedReason({
  detail,
  catalog,
  editContext,
}: {
  detail: Pick<ItineraryDetail, 'tenant'>;
  catalog?: Pick<ItineraryRecordCatalog, 'enabled'> | null;
  editContext?: Pick<ItineraryRecordEditContext, 'flags'> | null;
}): string | null {
  if (catalog?.enabled === false || editContext?.flags.catalog_edit_enabled === false) return CATALOG_EDITING_OFF;
  if (!detail.tenant) return CATALOG_NO_OPERATOR;
  return null;
}

/** The feed that writes the itinerary — by name — while its source may update itineraries;
 *  null when no feed does (created in the console, or a create-only source). */
export function itineraryManagedBy(ctx: Pick<ItineraryRecordEditContext, 'feed'>): string | null {
  const feed = ctx.feed.feed;
  return feed?.updates ? feed.source_name : null;
}

/** The catalog's choice questions as the editor asks them: the re-key question
 *  (`rekeyQuestionOptions`, the activity's) — its Cancel takes the Custom ID change back; the
 *  Custom ID a Re-key confirms in the identity rule's form. */
export function itineraryOptions(catalog: Pick<ItineraryRecordCatalog, 'options'>): RecordOption[] {
  return rekeyQuestionOptions(catalog.options);
}

/** Whether a raised question is asked before the preview says so: the re-key question only on
 *  an itinerary an import has touched (its newest import run, `source`) — the preview decides. */
export function itineraryOptionAsked(detail: Pick<ItineraryDetail, 'source'>) {
  return rekeyAskedUpFront(detail.source !== null);
}

/** Bring back the removed days a drift names (a field another editor changed since the
 *  editor loaded it: removing it would delete their edit) — each where it was stored, one undo
 *  step each — and say so. The sentence, or null when the drift named none of them. */
function bringBackDrifted(
  drifted: ReadonlyArray<{ entity: string; id: number }>,
  {
    store,
    dispatch,
    ownerRec,
    baselines,
    channel,
  }: {
    store: { getState: () => DraftState };
    dispatch: (action: DraftAction) => void;
    ownerRec: RecordKey;
    baselines: ItineraryBaselines;
    channel: DaysChannel | undefined;
  },
): string | null {
  const ids = driftedRemovedDays(store.getState(), ownerRec, drifted);
  if (ids.length === 0) return null;
  const names: string[] = [];
  for (const dayId of ids) {
    const ctx: OutlineOpsContext = {
      state: store.getState(),
      baselines,
      ownerRec,
      spec: DAYS_OUTLINE,
      memberships: ITINERARY_MEMBERSHIPS,
    };
    const done = restoreDay(ctx, dayId);
    if (!done) continue;
    dispatch(done.action);
    names.push(dayNameIn({ ...ctx, state: store.getState() }, dayId));
  }
  const said = removedDaysDrifted(names);
  channel?.said.emit(said);
  return said;
}

/** Where the days brought back are said for every eye: the ledger's refusal while the draft
 *  still holds changes (its alert), else a snackbar — an emptied draft's ledger shows none. */
function sayBroughtBack(said: string, state: DraftState, snackbar: { show: (m: string, s: 'warning') => void }): void {
  if (draftCount(state) === 0) snackbar.show(said, 'warning');
}

/** A drift conflict's key as a drifted change (`day:9002:title` → entity, id). */
function driftOfKey(key: string): { entity: string; id: number } | null {
  const [entity, id] = key.split(':');
  return entity && id !== undefined && /^-?\d+$/.test(id) ? { entity, id: Number(id) } : null;
}

/** The itinerary's changeset preview as the editor reads it. Drift the preview found on a
 *  removed day brings that day back (as the apply's 409 would), the edit context refetched. */
export function useItineraryPreview(
  id: number,
  body: RecordChangeset | null,
  baselines: ItineraryBaselines | null | undefined,
  channel?: DaysChannel,
): RecordPreviewQuery {
  const q = useItineraryChangesetPreview(id, body, baselines);
  const names = useStoredDayNames(id, baselines);
  // What a removal takes with it names each day as its row does, never by its database id.
  const data = useMemo(() => (q.data && names ? namedRemovalsInPreview(q.data, names) : q.data), [q.data, names]);
  const { store, dispatch } = useDraftStable();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  useEffect(() => {
    if (!q.data || !baselines) return;
    const drifted = q.data.conflicts.filter((c) => c.code === 'drift').flatMap((c) => driftOfKey(c.key) ?? []);
    const said = bringBackDrifted(drifted, { store, dispatch, ownerRec: itineraryRec(id), baselines, channel });
    if (!said) return;
    void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
    // The preview has no refusal of its own to show it in.
    snackbar.show(said, 'warning');
    // The plan is the input; the rest is stable for the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);
  return {
    data,
    isFetching: q.isFetching,
    isPlaceholderData: q.isPlaceholderData,
    error: q.error,
    // Asked nothing (no change in the body): settled, not loading.
    isEnabled: q.isEnabled,
  };
}

/** The picked records the detail names, by field — shown by name in both modes. */
export function itineraryOptionRefs(d: ItineraryDetail): Record<string, RecordOptionRef[]> {
  const named = (refs: readonly { id: number; name?: string | null }[], group: string, noun: string) =>
    refs.map((r) => ({ id: r.id, label: r.name?.trim() || `${noun} #${r.id}`, group }));
  return {
    currency: d.currency
      ? [
          {
            id: d.currency.id,
            label: d.currency.symbol && d.currency.symbol !== d.currency.code ? `${d.currency.code} (${d.currency.symbol})` : d.currency.code,
          },
        ]
      : [],
    cta_tour: d.cta_tour ? [{ id: d.cta_tour.id, label: d.cta_tour.name?.trim() || `Tour #${d.cta_tour.id}` }] : [],
    vehicle_route_set: d.vehicle_route_set
      ? [{ id: d.vehicle_route_set.id, label: d.vehicle_route_set.name?.trim() || `Line set #${d.vehicle_route_set.id}` }]
      : [],
    regions: named(d.regions, 'Regions', 'Region'),
    trip_types: named(d.trip_types, 'Filters', 'Filter'),
    tags: named(d.tags, 'Catalog search tags', 'Tag'),
    atag: named(d.atag, 'Access tags', 'Access tag'),
  };
}

/** Where a read-only value comes from — its tooltip. */
const PROVENANCE: Readonly<Record<string, string>> = {
  cover_image: 'The first gallery image — set by the gallery order',
  operator_cta_default: 'The operator’s button behaviour: what a button set to inherit does',
  ext_id: 'The import identity, derived from the custom ID',
  branchio_link: 'Made by Branch.io in the background',
  qr_image_url: 'The deep link’s stored QR image',
  source: 'The newest import that touched the itinerary',
  default_itinerary: 'The catalog itinerary (day plan) it shows, and the itineraries sharing it',
};

/** Help in the operator's words where the catalog's speaks to API clients (the staging
 *  endpoint). */
const FIELD_HELP: Readonly<Partial<Record<string, string>>> = {
  'itinerary:route_map':
    'The map shown with the itinerary in the app. JPG, PNG, GIF or WebP, at most 10 MB. A picked file uploads at ' +
    'once and becomes the route map when you save; the replaced one is deleted unless something else shows it.',
  'day:image':
    'Shown with the day in the app. JPG, PNG, GIF or WebP, at most 10 MB. A picked file uploads at once and ' +
    'becomes the day’s image when you save.',
};

/** A field as this page edits it: help in the operator's words, and a day's span on its own
 *  input — the catalog's integer, refusing what the changeset would (`DaySpanWidget`). */
const ownField = (f: RecordField): RecordField => {
  const help = FIELD_HELP[`${f.entity}:${f.key}`];
  const span = f.entity === ITINERARY_DAY_ENTITY && f.key === 'day_span' && f.widget !== null;
  return help || span ? { ...f, ...(help ? { help } : {}), ...(span ? { widget: DAY_SPAN_WIDGET } : {}) } : f;
};

const REGISTRY_FIELDS = [...itineraryRecordFields(), ...itineraryDayRecordFields()].map(ownField);
const NONE: never[] = [];

/** The stored days' names (`storedDayNames`) while edit_context's baselines are here. */
function useStoredDayNames(id: number, baselines: ItineraryBaselines | null | undefined): Map<number, string> | null {
  return useMemo(() => (baselines ? storedDayNames(baselines, itineraryRec(id)) : null), [id, baselines]);
}

/** A day's ledger group title, named like its row: a saved day by its stored row ("Day 2–3 ·
 *  Ayutthaya" — the stored order, spans and title); a new day by its place and title in the
 *  draft ("New day · Day 9", "New day · Day 9 · Kyoto"), so two untitled new days read apart.
 *  The draft's rows are worked out once per draft state. */
function dayGroupTitles(baselines: ItineraryBaselines, ownerRec: RecordKey) {
  const stored = storedDayNames(baselines, ownerRec);
  const rowsOf = new WeakMap<DraftState['entries'], DayRowModel[]>();
  const draftRows = (state: DraftState) => {
    let rows = rowsOf.get(state.entries);
    if (!rows) {
      rows = dayRows(state.entries, baselines, ownerRec);
      rowsOf.set(state.entries, rows);
    }
    return rows;
  };
  return (dayId: number, state: DraftState | undefined): string => {
    if (dayId > 0) return stored.get(dayId) ?? `Day #${dayId}`;
    const row = state ? draftRows(state).find((r) => r.id === dayId && !r.removed) : undefined;
    if (row) return `New day · ${dayRowName(row)}`;
    const t = state?.entries[dayRec(dayId)]?.title?.to;
    return typeof t === 'string' && t.trim() ? `New day · ${t.trim()}` : 'New day';
  };
}

/** The values the generic format cannot word: the months ("Any month"), an inherited button
 *  behaviour, the price. Picked records are named by `optionRefs` (the detail's, and what the
 *  operator picks), so a new pick reads by its name too. */
const OWN_FORMAT: ReadonlySet<string> = new Set(['months', 'cta_button_behavior', 'cost']);

/** A placeholder's fields the server locks (O9: its edit_context `blocked`) — said in their
 *  caption in both modes, so pressing E moves nothing and no tooltip covers the value. */
const PLACEHOLDER_LOCKED_FIELDS: ReadonlySet<string> = new Set(['name', 'published']);

/** The fields' standing notes: a placeholder's locked Name and Published (the Actions' reason,
 *  `PLACEHOLDER_LOCKED`), then the App button's (`itineraryFieldNote`). */
export function itineraryFieldNotes(
  detail: Pick<ItineraryDetail, 'id' | 'placeholder' | 'operator_cta_default'>,
): NonNullable<RecordEditorConfig['fieldNoteOf']> {
  const appButton = itineraryFieldNote(detail);
  const rec = itineraryRec(detail.id);
  return (r, field, valueOf) =>
    detail.placeholder && r === rec && field.entity === ITINERARY_ENTITY && PLACEHOLDER_LOCKED_FIELDS.has(field.key)
      ? { text: PLACEHOLDER_LOCKED, locks: true }
      : appButton(r, field, valueOf);
}

/** What both modes share: the detail's facts. */
function sharedParts(detail: ItineraryDetail): Omit<RecordEditorConfig, 'fields' | 'usePreview' | 'useApply' | 'widgets'> {
  return {
    options: NONE,
    actions: NONE,
    effects: NONE,
    timezone: ITINERARY_TIMEZONE,
    recordNoun: 'itinerary',
    managedBy: null,
    // Its sources sync: "Intrepid's next sync will undo this" (plan §7).
    feedEvent: 'sync',
    verdictOf: () => null,
    formatValue: (field, _rec, value) =>
      field.entity === ITINERARY_ENTITY && OWN_FORMAT.has(field.key) ? formatItineraryValue(field, value, detail) : undefined,
    provenanceOf: (field) => PROVENANCE[field.key],
    // A placeholder's locked Name and Published; the App button's fields its behaviour does
    // not use, the Phone behaviour's tel: link.
    fieldNoteOf: itineraryFieldNotes(detail),
    optionRefs: itineraryOptionRefs(detail),
  };
}

export interface ItineraryEditorInput {
  detail: ItineraryDetail;
  /** The server's edit catalog and the itinerary's edit context; until both are here, view
   *  mode from the registries and the detail. */
  catalog?: ItineraryRecordCatalog | null;
  editContext?: ItineraryRecordEditContext | null;
  /** A ledger row for a field that is not on screen (another page tab, a closed day). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
  /** What the editor tells the Days card (days brought back, the saved days' ids). */
  channel?: DaysChannel;
}

/** Save, with the days' own steps around the core's (`daySave`): the new days' real ids move
 *  the open day (the draft itself is re-keyed inside the ledger's SAVED, never as an undoable
 *  step of its own); a 409 on a removed day another editor changed meanwhile brings it back —
 *  and when nothing else drifted, the save's refusal says so in those words instead of asking
 *  for Keep mine / Take theirs on a field the draft does not hold. */
export function useItineraryApply(
  id: number,
  { onSaved }: { onSaved: (result: RecordApplyResult) => void },
  baselines: ItineraryBaselines | null,
  channel: DaysChannel | undefined,
): RecordApplyMutation {
  const { store, dispatch } = useDraftStable();
  const snackbar = useSnackbar();
  const ownerRec = itineraryRec(id);
  const names = useStoredDayNames(id, baselines);
  const m = useApplyItineraryChangeset(id, {
    // The receipt names the removed days as their rows did (the baselines the save was sent with).
    onSaved: (result) => onSaved(names ? namedRemovalsInResult(result, names) : result),
    baselines,
    onCreated: (created) => {
      channel?.created.emit(created);
    },
  });
  return {
    isPending: m.isPending,
    mutate: (body, opts) =>
      m.mutate(body, {
        onError: (e) => {
          if (!(e instanceof CatalogDriftError) || !baselines) {
            opts?.onError?.(e);
            return;
          }
          const removed = driftedRemovedDays(store.getState(), ownerRec, e.drifted);
          const said = bringBackDrifted(e.drifted, { store, dispatch, ownerRec, baselines, channel });
          if (!said) {
            opts?.onError?.(e);
            return;
          }
          const rest = e.drifted.filter((d) => !(d.entity === DAY_CHANGE_ENTITY && removed.includes(d.id)));
          sayBroughtBack(said, store.getState(), snackbar);
          opts?.onError?.(rest.length > 0 ? new CatalogDriftError(e.status, e.body, e.message, rest) : new Error(said));
        },
      }),
  };
}

/** The editing half (catalog + edit context), pure — for tests. */
export function itineraryEditorConfigParts({
  detail,
  catalog,
  editContext: ctx,
  onFocusMissing,
}: ItineraryEditorInput & {
  catalog: ItineraryRecordCatalog;
  editContext: ItineraryRecordEditContext;
}): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const rec = itineraryRec(detail.id);
  const blocked = itineraryEditBlockedReason({ detail, catalog, editContext: ctx });

  const verdictOf = (r: RecordKey, field: RecordField): RecordVerdict | null => {
    if (r === rec) return ctx.feed.verdicts[field.key] ?? null;
    // A day is matched by its title: its verdict is the title's.
    if (field.entity === ITINERARY_DAY_ENTITY && field.key === 'title') return ctx.feed.day_verdicts[r] ?? null;
    return null;
  };

  const editabilityOf = (r: RecordKey, field: RecordField): RecordEditability => {
    if (blocked) return { editable: false, reason: blocked };
    if (!ctx.permissions[ITINERARY_MANAGE_PERM]) {
      return { editable: false, reason: `Needs the ${ITINERARY_MANAGE_PERM} permission` };
    }
    const missing = field.permissions.find((p) => !ctx.permissions[p]);
    if (missing) return { editable: false, reason: `Needs the ${missing} permission` };
    const guards = ctx.blocked[`${r}:${field.key}`] ?? [];
    if (guards.length > 0) return { editable: false, reason: guards.map((g) => catalog.guards[g] ?? g).join(' ') };
    return { editable: true };
  };

  const title = itineraryTitle(detail);
  const dayGroupTitle = dayGroupTitles(ctx.baselines, rec);
  return {
    ...sharedParts(detail),
    fields: catalog.fields.map(catalogRecordField).map(ownField),
    options: itineraryOptions(catalog),
    isOptionAsked: itineraryOptionAsked(detail),
    effects: catalog.effects,
    managedBy: itineraryManagedBy(ctx),
    verdictOf,
    editabilityOf,
    groupOf: (r, state) => {
      const { entity, id } = parseRecordKey(r);
      if (entity !== DAY_CHANGE_ENTITY) return { id: ITINERARY_ENTITY, title };
      return { id: r, title: dayGroupTitle(id, state) };
    },
    // A day the draft adds reads a new day's values until edited.
    defaultValueOf: (r, path) => (parseRecordKey(r).entity === DAY_CHANGE_ENTITY ? NEW_DAY[path as keyof typeof NEW_DAY] : undefined),
    // Reverting the day list takes the new days with it — their own edits too.
    cascadeTargets: (r, path, state): DraftTarget[] =>
      r === rec && path === DAYS_FIELD
        ? Object.entries(state.entries).flatMap(([k, paths]) =>
            parseRecordKey(k).entity === DAY_CHANGE_ENTITY && parseRecordKey(k).id < 0
              ? Object.keys(paths).map((p) => ({ rec: k, path: p }))
              : [],
          )
        : [],
    // The day list: a save's new days take their real ids in it (SAVED's `created`).
    memberships: ITINERARY_MEMBERSHIPS,
    recordOrder: [rec, ...detail.days.map((d) => dayRec(d.id))],
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The stored images the image inputs show: edit_context's Image ids drawn from the detail's
 *  CDN keys (none before edit_context). */
export function itineraryStoredImages(
  detail: ItineraryDetail,
  ctx: Pick<ItineraryRecordEditContext, 'baselines'> | null | undefined,
): ItineraryStoredImages {
  return {
    routeMap: () => storedImageOf(ctx?.baselines[itineraryRec(detail.id)]?.route_map, detail.route_map),
    dayImage: (dayId) =>
      storedImageOf(ctx?.baselines[dayRec(dayId)]?.image, detail.days.find((d) => d.id === dayId)?.image ?? null),
  };
}

/** The itinerary's `RecordEditorConfig` — stable while its inputs are. */
export function useItineraryEditorConfig({
  detail,
  catalog,
  editContext,
  onFocusMissing,
  channel,
}: ItineraryEditorInput): RecordEditorConfig {
  const widgets = useItineraryWidgets(detail, itineraryStoredImages(detail, editContext));
  const id = detail.id;
  const baselines = editContext?.baselines ?? null;
  return useMemo<RecordEditorConfig>(() => {
    // The same hooks in both halves (React's hook order): until the editor's data is here the
    // page is in view mode, where the editor asks no preview and nothing saves.
    const hooks: Pick<RecordEditorConfig, 'usePreview' | 'useApply'> = {
      usePreview: (body) => useItineraryPreview(id, body, baselines, channel),
      useApply: (opts) => useItineraryApply(id, opts, baselines, channel),
    };
    if (!catalog || !editContext) {
      return { ...sharedParts(detail), fields: REGISTRY_FIELDS, description: false, widgets, ...hooks };
    }
    return { ...itineraryEditorConfigParts({ detail, catalog, editContext, onFocusMissing }), widgets, ...hooks };
  }, [detail, catalog, editContext, onFocusMissing, widgets, id, baselines, channel]);
}
